import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { attachUninterceptedErrorReporting } from '../services/apiErrorReporting'

export const useSessionStore = defineStore('session', () => {
  const user = ref(null)
  const organization = ref(null)
  // SSR (build-time prerender): no localStorage -> render logged out.
  const loggedIn = ref(typeof localStorage !== 'undefined' ? localStorage.getItem('logged_in') : null)
  const device = ref(null)
  // Whether `device` is an ANSWER for this browser or merely not fetched yet.
  // `null` alone cannot tell "this browser is not a registered device" from
  // "nobody has asked": the in-app login paths go through `set_session`, which
  // carries no device, and pages must not offer "Register this computer" to a
  // browser that is already registered just because the answer has not
  // arrived. Only GET /user_session (or an add/delete of this browser's device)
  // makes it true.
  const deviceLoaded = ref(false)
  const transitioning = ref(false)
  const lti_launch = ref(null)
  
  // Track if the password compromise banner has been dismissed for this session
  const passwordCompromiseBannerDismissed = ref(false)
  
  // Computed property to check if user's password is compromised
  const passwordCompromised = computed(() => {
    return user.value?.password_compromised === true
  })
  
  // Computed property to determine if the warning banner should be shown
  const showPasswordCompromiseBanner = computed(() => {
    return passwordCompromised.value && !passwordCompromiseBannerDismissed.value
  })
  
  // Organizations whose security banner (GitLab #1072) has already been shown
  // since the last login -- on the login page itself, or dismissed from the
  // app header. Kept in sessionStorage so a reload in the same tab does not
  // bring it back; cleared on logout so the next login shows it again.
  // Storage access can throw (a sandboxed LTI iframe, privacy modes), and a
  // banner that shows again is the harmless failure, so errors fall back to
  // memory only.
  const SECURITY_BANNER_SEEN_KEY = 'security_banner_seen'
  const readSecurityBannerSeen = () => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(SECURITY_BANNER_SEEN_KEY) || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  const securityBannerSeen = ref(readSecurityBannerSeen())
  const writeSecurityBannerSeen = () => {
    try {
      if (securityBannerSeen.value.length) {
        sessionStorage.setItem(SECURITY_BANNER_SEEN_KEY, JSON.stringify(securityBannerSeen.value))
      } else {
        sessionStorage.removeItem(SECURITY_BANNER_SEEN_KEY)
      }
    } catch {
      // Memory-only for this tab; see above.
    }
  }
  const markSecurityBannerSeen = (organizationUid) => {
    if (!organizationUid || securityBannerSeen.value.includes(organizationUid)) return
    securityBannerSeen.value = [...securityBannerSeen.value, organizationUid]
    writeSecurityBannerSeen()
  }
  // The organization banner to show in the app header, or null.
  const securityBannerText = computed(() => {
    const org = organization.value
    if (!user.value || !org || !org.security_banner_text) return null
    return securityBannerSeen.value.includes(org.uid) ? null : org.security_banner_text
  })

  // Function to dismiss the banner for this session
  const dismissPasswordCompromiseBanner = () => {
    passwordCompromiseBannerDismissed.value = true
  }
  
  // Function to clear the password compromised flag (called after password change)
  const clearPasswordCompromisedFlag = () => {
    if (user.value) {
      user.value.password_compromised = false
      user.value.password_compromised_at = null
    }
    passwordCompromiseBannerDismissed.value = false
  }

  const set_session = (sessionData) => {
    console.log('SessionStore: Setting new session', sessionData)
    console.log('SessionStore: Old user:', user.value)
    console.log('SessionStore: Old organization:', organization.value)
    
    user.value = sessionData.user
    organization.value = sessionData.organization
    lti_launch.value = sessionData.lti_launch || null
    loggedIn.value = true
    localStorage.setItem('logged_in', true)
    
    console.log('SessionStore: New user:', user.value)
    console.log('SessionStore: New organization:', organization.value)
    console.log('SessionStore: LTI launch:', lti_launch.value)
  }

  const clear_session = () => {
    user.value = null
    organization.value = null
    lti_launch.value = null
    // The registered device is per-browser, not per-user, but it is read from
    // THIS session's /user_session response: leaving it set after a logout
    // leaves the next viewer's menu describing a device nobody has re-fetched.
    // `forget_device` clears it too, for the same reason.
    device.value = null
    deviceLoaded.value = false
    loggedIn.value = false
    passwordCompromiseBannerDismissed.value = false
    securityBannerSeen.value = []
    writeSecurityBannerSeen()
    localStorage.removeItem('logged_in')
  }

  let _fetchPromise = null

  const fetchSession = async (force = false) => {
    // Return cached session unless a refresh is forced. A force is needed when
    // the server-side session changed underneath us — e.g. after switching
    // schools, where the active organization (and the user's roles for it)
    // change and must be re-fetched.
    if (!force && user.value) {
      return { user: user.value, organization: organization.value, lti_launch: lti_launch.value }
    }

    // Deduplicate concurrent calls — but a FORCED fetch must be a request
    // initiated no earlier than now. Joining an in-flight request breaks the
    // force contract: the boot probe's cookie-racing 401 can be in flight
    // when PaymentComplete forces a refresh, and returning that doomed
    // promise re-creates the paid-user payment bounce on high-latency
    // connections (test incident, 2026-08-15). When forced, wait out the
    // in-flight request (its result doesn't matter) and start fresh.
    if (_fetchPromise) {
      if (!force) return _fetchPromise
      try { await _fetchPromise } catch { /* superseded by the fresh fetch */ }
    }

    _fetchPromise = (async () => {
      try {
        // Deliberately not restapi.js's error_intercept — a /user_session 401
        // must not fire that interceptor's toast/redirect from inside every
        // caller of fetchSession. It does still get reporting-only triage, so
        // an outage on this endpoint reaches Error Tracking with its
        // endpoint/method/status instead of vanishing.
        const api = attachUninterceptedErrorReporting(axios.create({
          baseURL: import.meta.env.VITE_API_URL,
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        }))

        const response = await api.get('/user_session')
        // A 200 whose body is not a session (an HTML error page from a proxy
        // or CDN, say) is a failed fetch like any non-200, not a bug in this
        // code: reject it the same way instead of crashing on `.user` of
        // undefined, so callers can tell it apart from a programming error.
        if (response.status === 200 && response.data && typeof response.data.session === 'object' && response.data.session) {
          const sessionData = response.data.session
          user.value = sessionData.user
          organization.value = sessionData.organization
          device.value = response.data.device || null
          deviceLoaded.value = true
          lti_launch.value = sessionData.lti_launch || null
          loggedIn.value = true
          localStorage.setItem('logged_in', true)
          return sessionData
        }
        throw new Error('Failed to fetch session')
      } finally {
        _fetchPromise = null
      }
    })()

    return _fetchPromise
  }

  // Record this browser's device after the server has just changed it: the
  // `device` returned by POST /add_device (whose response also set the
  // cookie), or null after DELETE /device/{uid} cleared it. The answer is then
  // known without another /user_session round trip.
  const setDevice = (value) => {
    device.value = value || null
    deviceLoaded.value = true
  }

  // Resolve `device` when it is not known yet. Normally it already is -- the
  // app's boot fetch of /user_session carries it -- and this costs nothing.
  // It only issues a request when the session was established in-app through
  // `set_session` (login, OTP, passkey), which carries no device: then it
  // re-reads /user_session once, because the `device_id` cookie is HttpOnly
  // and the API is the only thing that can say which device this browser is.
  const ensureDevice = async () => {
    if (deviceLoaded.value) return device.value
    if (_fetchPromise) {
      try { await _fetchPromise } catch { /* fall through to a fresh fetch */ }
      if (deviceLoaded.value) return device.value
    }
    await fetchSession(true)
    return device.value
  }

  return {
    user,
    organization,
    loggedIn,
    device,
    deviceLoaded,
    setDevice,
    ensureDevice,
    transitioning,
    lti_launch,
    passwordCompromised,
    showPasswordCompromiseBanner,
    passwordCompromiseBannerDismissed,
    set_session,
    clear_session,
    fetchSession,
    dismissPasswordCompromiseBanner,
    clearPasswordCompromisedFlag,
    securityBannerText,
    markSecurityBannerSeen
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSessionStore, import.meta.hot))
}